import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { Button, Drawer, Loader, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { IconAlertCircle } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BILLING_DISPLAY_TIMEZONE } from '@/pages/Billing/utils'
import type { OrpcClient } from '@/orpc/client'
import { managedCaseRefusal, type PatientInvoiceKind } from '@/model/middleware-types'
import { PATIENT_INVOICE_KIND_LABELS } from '@/model/patient-invoice-kind'
import { useOrpc } from '@/hooks/useClient'
import { PAYMENT_STATUS_OPTIONS } from '../state'
import {
  PILL_STYLE_AMBER,
  PILL_STYLE_GRAY,
  PILL_STYLE_GREEN,
  PILL_STYLE_RED,
  formatCalendarDate,
  formatCents,
  type StatusPillStyle,
} from '../utils'
import { AddPatientPaymentForm, type PatientPaymentMode } from './AddPatientPaymentForm'
import { ClaimStatusControl } from './ClaimStatusControl'
import { EventHistory } from './EventHistory'
import {
  PatientResponsibilitySection,
  type PatientPaymentAction,
} from './PatientResponsibilitySection'
import { PaymentCards } from './PaymentCards'
import { ReconciliationSummary } from './ReconciliationSummary'
import { RecordPaymentForm } from './RecordPaymentForm'
import { StatusPill } from './StatusPill'

dayjs.extend(utc)
dayjs.extend(timezone)

export type BillingCaseDetail = Awaited<ReturnType<OrpcClient['billingV2CaseDetail']>>

type CaseDrawerProps = {
  /** Encounter whose case the drawer shows; null keeps the drawer closed. */
  encounterId: string | null
  onClose: () => void
}

function paymentStatusLabel(status: string): string {
  return PAYMENT_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status
}

function paymentStatusStyle(status: string): StatusPillStyle {
  if (status === 'paid') return PILL_STYLE_GREEN
  if (status === 'failed') return PILL_STYLE_RED
  if (status === 'no_payment') return PILL_STYLE_GRAY
  return PILL_STYLE_AMBER
}

function SectionTitle({ children }: { children: string }) {
  return (
    <Text size="sm" fw={600} c="dimmed">
      {children}
    </Text>
  )
}

// Submission is button-driven (the Routing column's Submit claim/Retry → POST
// /v2/billing/case/submit-claim → the claim-submit task). This section only ever displays
// state: the dimmed "Submitted" line once claimSubmittedAt is set (NOT externalClaimId —
// a non-null external id with claim_submitted_at still null is a normal, resumable
// mid-chain state, not "done"; see case-repository.ts's recordServiceId doc), a dimmed
// "Submission in progress" line for that mid-chain state, and submitLastError
// independently (a failed submission surfaces its reason here; the Retry button lives in
// the Routing column). When submitLastError is set, "Submission in progress" is
// deliberately omitted: a case with a recorded failure isn't "in progress", so showing
// both would read as contradictory — the error alert alone is the honest state.
function SubmissionSection({
  externalClaimId,
  claimSubmittedAt,
  submitLastError,
}: {
  externalClaimId: string | null
  claimSubmittedAt: string | null
  submitLastError: string | null
}) {
  if (!externalClaimId && !submitLastError) return null

  return (
    <div className="flex flex-col gap-2">
      <SectionTitle>Submission</SectionTitle>

      {claimSubmittedAt ? (
        <Text size="xs" c="dimmed">
          {`Submitted ${dayjs(claimSubmittedAt).tz(BILLING_DISPLAY_TIMEZONE).format('MM/DD/YYYY')}`}
        </Text>
      ) : externalClaimId && !submitLastError ? (
        <Text size="xs" c="dimmed">
          Submission in progress
        </Text>
      ) : null}

      {submitLastError ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <IconAlertCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <Text size="sm" c="red.8">
            {submitLastError}
          </Text>
        </div>
      ) : null}
    </div>
  )
}

function DrawerContent({
  detail,
  onRecordPayment,
  onPatientPaymentAction,
}: {
  detail: BillingCaseDetail
  onRecordPayment: () => void
  onPatientPaymentAction: (kind: PatientInvoiceKind, action: PatientPaymentAction) => void
}) {
  const { case: billingCase, patient, insurance, reconciliation } = detail

  // PD-1739: a submitted case is owned by the clearinghouse — the write side (EOB attach,
  // payment form, Record) is not offered; the backend refuses it with the same text anyway.
  const isManaged = Boolean(billingCase.claimSubmittedAt)

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col">
          <Text size="md" fw={700} c="dark">
            {patient.name}
          </Text>
          {patient.dob ? (
            <Text size="xs" c="dimmed">
              DOB: {formatCalendarDate(patient.dob)}
            </Text>
          ) : null}
          {billingCase.dos ? (
            <Text size="xs" c="dimmed">
              DOS: {dayjs(billingCase.dos).tz(BILLING_DISPLAY_TIMEZONE).format('MM/DD/YYYY')}
            </Text>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <ClaimStatusControl
            label="Claim"
            encounterId={billingCase.encounterId}
            currentStatus={billingCase.claimStatus}
            hasPayments={
              reconciliation.insurancePaidCents > 0 || (reconciliation.patientPaidCents ?? 0) > 0
            }
            managedRoute={isManaged ? billingCase.submissionRoute : undefined}
          />
          <StatusPill label="Payment" style={paymentStatusStyle(billingCase.paymentStatus)}>
            {paymentStatusLabel(billingCase.paymentStatus)}
          </StatusPill>
        </div>
      </div>

      <SubmissionSection
        externalClaimId={billingCase.externalClaimId}
        claimSubmittedAt={billingCase.claimSubmittedAt}
        submitLastError={billingCase.submitLastError}
      />

      {insurance ? (
        <div className="flex flex-col gap-1">
          <SectionTitle>Insurance</SectionTitle>
          {insurance.payerName ? (
            <Text size="sm" c="dark">
              {insurance.payerName}
            </Text>
          ) : null}
          {insurance.planName ? (
            <Text size="sm" c="dark">
              {insurance.planName}
            </Text>
          ) : null}
          {insurance.memberId ? (
            <Text size="xs" c="dimmed">
              Member ID: {insurance.memberId}
            </Text>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <SectionTitle>Patient responsibility</SectionTitle>
        <PatientResponsibilitySection
          invoices={detail.patientInvoices}
          patientRespCents={reconciliation.patientRespCents}
          patientPaidCents={reconciliation.patientPaidCents}
          patientBalanceCents={reconciliation.patientBalanceCents}
          selfPay={detail.selfPay}
          onAction={onPatientPaymentAction}
        />
      </div>

      <div className="flex flex-col gap-2">
        <SectionTitle>Reconciliation</SectionTitle>
        <ReconciliationSummary reconciliation={reconciliation} />
      </div>

      <div className="flex flex-col gap-2">
        <SectionTitle>Insurance payments</SectionTitle>
        <PaymentCards payments={detail.payments} />
        <div>
          {isManaged ? (
            <Text size="sm" c="dimmed">
              {managedCaseRefusal(billingCase.submissionRoute)}
            </Text>
          ) : (
            <Button variant="light" size="xs" onClick={onRecordPayment}>
              Record insurance payment
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <SectionTitle>History</SectionTitle>
        <EventHistory events={detail.events} />
      </div>
    </div>
  )
}

// The Claim & Payment Details drawer: fetches the Task 11 case detail and lays
// out the read blocks per the mockups (mirrors the legacy BillingDetailsDrawer
// structure without importing it). "Record insurance payment" switches to the
// write side (Task 20) — the EOB entry + payment form; success lands back on
// the read view with the detail query invalidated by the form.
export function CaseDrawer({ encounterId, onClose }: CaseDrawerProps) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'read' | 'record' | 'patient-payment'>('read')
  const [preset, setPreset] = useState<{ kind: PatientInvoiceKind; mode: PatientPaymentMode }>({
    kind: 'copay',
    mode: 'charge',
  })

  /** A write form holds unsaved input — closing the drawer asks first. */
  const [dirty, setDirty] = useState(false)

  const refund = useMutation(orpc.refundCopay.mutationOptions())

  // A different case (or a reopen) always starts on the read view.
  useEffect(() => {
    setMode('read')
  }, [encounterId])

  const { data, isLoading, error } = useQuery({
    ...orpc.billingV2CaseDetail.queryOptions({
      input: { encounterId: encounterId ?? '' },
    }),
    enabled: encounterId !== null,
  })

  const confirmRefund = (kind: PatientInvoiceKind) => {
    const invoice = data?.patientInvoices.find((candidate) => candidate.kind === kind)
    if (!invoice || encounterId === null) return

    const label = PATIENT_INVOICE_KIND_LABELS[kind]

    modals.openConfirmModal({
      title: invoice.manual ? 'Mark as refunded' : `Refund ${label.toLowerCase()}`,
      children: invoice.manual ? (
        <Text size="sm">
          Mark this {formatCents(invoice.amountCents)} {label.toLowerCase()} as refunded? It was
          recorded manually — no Stripe refund will be made.
        </Text>
      ) : (
        <Text size="sm">
          Refund the full {formatCents(invoice.amountCents)} {label.toLowerCase()} to the
          patient&apos;s card? Stripe will email the refund receipt to the patient.
        </Text>
      ),
      labels: invoice.manual
        ? { confirm: 'Mark as refunded', cancel: 'Cancel' }
        : { confirm: 'Refund', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        void (async () => {
          try {
            await refund.mutateAsync({ encounterId, kind })
            void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
            void queryClient.invalidateQueries({ queryKey: orpc.getCopayStatus.key() })
            showNotification({ color: 'green', message: `${label} refunded in full.` })
          } catch (err) {
            showNotification({
              color: 'red',
              message:
                err instanceof Error ? err.message : `Unable to refund the ${label.toLowerCase()}.`,
            })
          }
        })()
      },
    })
  }

  const handlePatientPaymentAction = (kind: PatientInvoiceKind, action: PatientPaymentAction) => {
    if (action === 'refund') {
      confirmRefund(kind)
      return
    }

    // Add and Collect both open the form on the kind; how to collect is chosen there.
    setPreset({ kind, mode: 'charge' })
    setMode('patient-payment')
  }

  // Click outside, Esc and the X all land here. A half-filled payment form is easy to lose
  // that way, so it asks; Cancel inside the form stays immediate (a deliberate choice).
  const requestClose = () => {
    if (!dirty) {
      onClose()
      return
    }

    modals.openConfirmModal({
      title: 'You have unsaved changes',
      children: <Text size="sm">Are you sure you want to close? Your changes will be lost.</Text>,
      labels: { confirm: 'Discard changes', cancel: 'Stay on page' },
      confirmProps: { color: 'red' },
      onConfirm: onClose,
    })
  }

  return (
    <Drawer
      opened={encounterId !== null}
      onClose={requestClose}
      title="Claim & Payment Details"
      position="right"
      size="lg"
      closeButtonProps={{ 'aria-label': 'Close drawer' }}
    >
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader size="sm" />
        </div>
      ) : error ? (
        <Text size="sm" c="red">
          Failed to load case details
        </Text>
      ) : data ? (
        mode === 'record' && encounterId !== null ? (
          <RecordPaymentForm
            encounterId={encounterId}
            providerChargeCents={data.reconciliation.providerChargeCents}
            patientPaidCents={data.reconciliation.patientPaidCents}
            insurancePaidCents={data.reconciliation.insurancePaidCents}
            patientInvoices={data.patientInvoices}
            payerName={data.insurance?.payerName}
            onDirtyChange={setDirty}
            onSuccess={() => setMode('read')}
            onCancel={() => setMode('read')}
          />
        ) : mode === 'patient-payment' && encounterId !== null ? (
          <AddPatientPaymentForm
            encounterId={encounterId}
            invoices={data.patientInvoices}
            patientRespCents={data.reconciliation.patientRespCents}
            selfPay={data.selfPay}
            initialKind={preset.kind}
            initialMode={preset.mode}
            onDirtyChange={setDirty}
            onSuccess={() => setMode('read')}
            onCancel={() => setMode('read')}
          />
        ) : (
          <DrawerContent
            detail={data}
            onRecordPayment={() => setMode('record')}
            onPatientPaymentAction={handlePatientPaymentAction}
          />
        )
      ) : null}
    </Drawer>
  )
}
